import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '📘',
  number: 'Tomo 1',
  title: 'Geopolítica y Ciencia del Cambio Climático',
  subtitle: 'La base científica y política que tus alumnos necesitan para entender la crisis del siglo XXI',
  problem: 'El primer contacto con el cambio climático puede ser el más transformador o el más abrumador. Este tomo convierte un tema científico complejo en algo comprensible y urgente para tus alumnos desde el primer día con datos reales de su región.',
  bullets: ['Cómo funciona el efecto invernadero y por qué se intensificó', 'Los gases de efecto invernadero y sus fuentes en Latinoamérica', 'El fenómeno ENOS y sus impactos en cada país de la región', 'El Acuerdo de París, las NDCs y lo que cada país comprometió', 'Estrategias de mitigación y adaptación con ejemplos regionales', 'Cómo manejar la eco-ansiedad y el negacionismo climático en el aula'],
  result: 'Tus alumnos entienden el cambio climático como una realidad que ya está ocurriendo en su país, no como un problema lejano que afecta solo a los polos.'
}, {
  icon: '📗',
  number: 'Tomo 2',
  title: 'Biodiversidad y Ecosistemas Neotropicales',
  subtitle: 'La riqueza natural incomparable de Latinoamérica y las amenazas que la acechan',
  problem: 'Este es uno de los puntos donde más se desconectan los alumnos porque la biodiversidad parece abstracta. Este tomo la hace concreta, local y emocionalmente poderosa con especies y ecosistemas de cada país.',
  bullets: ['Los grandes biomas neotropicales: Amazonía, páramos, manglares, arrecifes, Chaco', 'La sexta extinción masiva: causas, cifras y ejemplos por país', 'Servicios ecosistémicos: qué le debemos a la naturaleza sin saberlo', 'Conservación y restauración: las buenas noticias que también existen', 'Los pueblos indígenas como guardianes de la biodiversidad', 'Biodiversidad y cambio climático: la doble amenaza'],
  result: 'Tus alumnos desarrollan un vínculo emocional con la biodiversidad de su región y comprenden por qué su pérdida los afecta directamente.'
}, {
  icon: '📙',
  number: 'Tomo 3',
  title: 'Gestión de Recursos, Energía y Economía Circular',
  subtitle: 'Del modelo lineal que destruye al modelo circular que transforma',
  problem: 'La economía circular suele sonar abstracta hasta que conecta con el teléfono celular, la ropa y la botella de agua que los alumnos usan todos los días. Este tomo hace esa conexión de manera inmediata y poderosa.',
  bullets: ['El modelo lineal tomar-hacer-usar-tirar y sus límites planetarios', 'La economía circular y las 9R explicadas con ejemplos locales', 'Energías renovables: solar, eólica, geotérmica, litio — el potencial de cada país', 'La crisis del agua dulce y los acuíferos de la región', 'Gestión de residuos sólidos: el panorama real de cada país', 'El triángulo del litio y la transición energética global'],
  result: 'Tus alumnos pasan de consumidores pasivos a ciudadanos que entienden el sistema económico que genera la crisis ambiental y las alternativas concretas disponibles.'
}, {
  icon: '📕',
  number: 'Tomo 4',
  title: 'Sostenibilidad Urbana y Huella Ecológica',
  subtitle: 'El módulo que más conecta con la vida cotidiana de tus alumnos',
  problem: 'La mayoría de los alumnos vive en ciudades. Este tomo lleva la crisis ambiental al barrio, a la calle y al edificio donde viven. No hay nada más poderoso para generar conciencia que hablar de lo que los rodea todos los días.',
  bullets: ['La ciudad como ecosistema: islas de calor, metabolismo urbano, contaminación', 'Movilidad sostenible: BRT, ciclovías, transporte público, emisiones del tráfico', 'Arquitectura bioclimática y eficiencia energética en edificios', 'La huella ecológica: cómo calcularla y qué significa ese número', 'Naturaleza urbana: árboles, huertos y biodiversidad en la ciudad', 'Ciudades resilientes ante el cambio climático'],
  result: 'Tus alumnos calculan su propia huella ecológica y entienden que el cambio ambiental empieza en las decisiones cotidianas de las ciudades donde viven.',
  popular: true
}, {
  icon: '📓',
  number: 'Tomo 5',
  title: 'Ética Ambiental, Legislación y Ciudadanía Activa',
  subtitle: 'Las herramientas legales y ciudadanas para actuar en el mundo real',
  problem: 'Este tomo cierra el ciclo más importante de la educación ambiental: el paso del conocimiento a la acción. Aquí tus alumnos descubren que tienen derechos, que la naturaleza tiene derechos y que existen herramientas concretas para defenderlos.',
  bullets: ['Las tres corrientes de la ética ambiental: antropocentrismo, biocentrismo y ecocentrismo', 'El marco legal ambiental de los 14 países de la región', 'Los derechos de la naturaleza: Ecuador, Bolivia, Colombia y más', 'El Acuerdo de Escazú y los tres derechos fundamentales del ciudadano ambiental', 'Movimientos sociales y defensores ambientales de Latinoamérica', 'Herramientas concretas de participación ciudadana disponibles en cada país'],
  result: 'Tus alumnos terminan el año sabiendo no solo qué está pasando con el ambiente sino qué pueden hacer ellos para cambiarlo desde su lugar como ciudadanos.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-beige-light" data-id="element-292">
      <div className="max-w-6xl mx-auto" data-id="element-293">
        <div className="text-center mb-16" data-id="element-294">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-green-dark mb-4" data-id="element-295">
            📘 Los 5 tomos del Mega Pack
          </h2>
          <p className="text-xl text-green-medium" data-id="element-296">
            Material completo para el aula
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-297">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-white rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-dark-border/10'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-298">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-white font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-299">
                  EL MÁS POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-300">
                <span className="text-4xl" data-id="element-301">{tomo.icon}</span>
                <div data-id="element-302">
                  <div className="text-gold font-bold text-sm uppercase tracking-wider" data-id="element-303">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-green-dark leading-tight mt-1" data-id="element-304">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-dark/80 font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-305">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-306">
                <h4 className="text-sm font-bold text-green-medium uppercase tracking-wider mb-2" data-id="element-307">
                  ¿Qué resuelve en el aula?
                </h4>
                <p className="text-dark/70 text-sm leading-relaxed" data-id="element-308">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-309">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-310">
                    <CheckCircle2 size={18} className="text-green-medium shrink-0 mt-0.5" data-id="element-311" />
                    <span className="text-dark/80 text-sm" data-id="element-312">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-green-light/50 border border-green-medium/20 rounded-xl p-4 mt-auto" data-id="element-313">
                <span className="text-green-dark font-bold block mb-1" data-id="element-314">
                  Resultado:
                </span>
                <p className="text-dark/80 text-sm" data-id="element-315">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}